import React, { useState, useMemo, useEffect } from 'react';
import { useJobRoles, useCreateJobRole, useUpdateJobRole, useDeleteJobRole } from '../../hooks/useAdmin';
import { useDocumentTypes } from '../../hooks/useAdmin';
import { apiClient } from '../../services/api';
import { API_ENDPOINTS } from '../../utils/constants';
import TableHeader from '../../components/common/TableHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import './AdminJobRoles.css';

const AdminJobRoles = () => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [roleDocuments, setRoleDocuments] = useState({}); // { roleId: [documents] }
  const [loadingDocuments, setLoadingDocuments] = useState({});
  
  const { data: jobRoles, isLoading } = useJobRoles();
  const { data: documentTypes } = useDocumentTypes({ is_active: true });
  const createMutation = useCreateJobRole();
  const updateMutation = useUpdateJobRole();
  const deleteMutation = useDeleteJobRole();

  // Fetch documents for each job role
  useEffect(() => {
    if (jobRoles && jobRoles.length > 0) {
      jobRoles.forEach(async (role) => {
        if (!roleDocuments[role.id] && !loadingDocuments[role.id]) {
          setLoadingDocuments(prev => ({ ...prev, [role.id]: true }));
          try {
            const response = await apiClient.get(API_ENDPOINTS.JOB_ROLES.DOCUMENTS(role.id));
            setRoleDocuments(prev => ({
              ...prev,
              [role.id]: response.mapped_documents || []
            }));
          } catch (error) {
            console.error(`Error fetching documents for role ${role.id}:`, error);
            setRoleDocuments(prev => ({ ...prev, [role.id]: [] }));
          } finally {
            setLoadingDocuments(prev => ({ ...prev, [role.id]: false }));
          }
        }
      });
    }
  }, [jobRoles, roleDocuments, loadingDocuments]);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedJobRoles = useMemo(() => {
    if (!jobRoles || !sortConfig.key) return jobRoles;
    
    return [...jobRoles].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = (a.job_role_name || a.name || '').toLowerCase();
          bVal = (b.job_role_name || b.name || '').toLowerCase();
          break;
        case 'hourly_rate':
          aVal = parseFloat(a.hourly_rate) || 0;
          bVal = parseFloat(b.hourly_rate) || 0;
          break;
        case 'documents':
          aVal = (roleDocuments[a.id]?.length || 0);
          bVal = (roleDocuments[b.id]?.length || 0);
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [jobRoles, sortConfig, roleDocuments]);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    job_role_key: '',
    job_role_name: '',
    hourly_rate: '',
    is_active: true,
    selectedDocuments: [] // Array of { document_id, is_required }
  });

  const handleOpenModal = async (role = null) => {
    if (role) {
      setEditingRole(role);
      
      // Fetch full job role details (including hourly_rate) from API
      let roleDetails = role;
      try {
        const detailsResponse = await apiClient.get(API_ENDPOINTS.JOB_ROLES.BY_ID(role.id));
        roleDetails = detailsResponse;
      } catch (error) {
        console.error('Error fetching job role details:', error);
        // Fall back to role data from list if API call fails
      }
      
      setFormData({
        job_role_key: roleDetails.job_role_key || '',
        job_role_name: roleDetails.job_role_name || roleDetails.name || '',
        hourly_rate: roleDetails.hourly_rate ? roleDetails.hourly_rate.toString() : '',
        is_active: roleDetails.is_active !== undefined ? roleDetails.is_active : true,
        selectedDocuments: []
      });
      
      // Fetch existing documents for this role
      try {
        const response = await apiClient.get(API_ENDPOINTS.JOB_ROLES.DOCUMENTS(role.id));
        const mappedDocs = (response.mapped_documents || []).map(mapping => ({
          document_id: mapping.document?.id || mapping.document_id,
          is_required: mapping.is_required !== undefined ? mapping.is_required : true
        }));
        setFormData(prev => ({ ...prev, selectedDocuments: mappedDocs }));
      } catch (error) {
        console.error('Error fetching role documents:', error);
      }
    } else {
      setEditingRole(null);
      setFormData({
        job_role_key: '',
        job_role_name: '',
        hourly_rate: '',
        is_active: true,
        selectedDocuments: []
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    setFormData({
      job_role_key: '',
      job_role_name: '',
      hourly_rate: '',
      is_active: true,
      selectedDocuments: []
    });
  };

  const handleDocumentToggle = (documentId, isRequired = true) => {
    setFormData(prev => {
      const existingIndex = prev.selectedDocuments.findIndex(doc => doc.document_id === documentId);
      if (existingIndex >= 0) {
        // Remove if already selected
        return {
          ...prev,
          selectedDocuments: prev.selectedDocuments.filter(doc => doc.document_id !== documentId)
        };
      } else {
        // Add new
        return {
          ...prev,
          selectedDocuments: [...prev.selectedDocuments, { document_id: documentId, is_required: isRequired }]
        };
      }
    });
  };

  const handleDocumentRequiredToggle = (documentId) => {
    setFormData(prev => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.map(doc =>
        doc.document_id === documentId
          ? { ...doc, is_required: !doc.is_required }
          : doc
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare job role data
      const jobRoleData = {
        job_role_key: formData.job_role_key || formData.job_role_name.toLowerCase().replace(/\s+/g, '_'),
        job_role_name: formData.job_role_name,
        hourly_rate: parseFloat(formData.hourly_rate),
        is_active: formData.is_active
      };
      
      let roleId;
      
      if (editingRole) {
        // Update existing role
        const updated = await updateMutation.mutateAsync({ id: editingRole.id, data: jobRoleData });
        roleId = editingRole.id;
      } else {
        // Create new role
        const created = await createMutation.mutateAsync(jobRoleData);
        roleId = created.id;
      }
      
      // Handle document mappings
      const documentMappingErrors = [];
      if (roleId) {
        // Get existing mappings
        let existingMappings = [];
        try {
          const response = await apiClient.get(`/job-role-documents/?job_role_id=${roleId}`);
          // Handle both array response and object with results property
          existingMappings = Array.isArray(response) ? response : (response.results || []);
        } catch (error) {
          console.error('Error fetching existing mappings:', error);
          existingMappings = [];
        }
        
        // Get list of selected document IDs
        const selectedDocIds = formData.selectedDocuments.map(d => d.document_id);
        
        // Remove mappings that are no longer selected
        for (const mapping of existingMappings) {
          const docId = mapping.document?.id || mapping.document_id || mapping.document;
          if (!selectedDocIds.includes(docId)) {
            try {
              await apiClient.delete(`/job-role-documents/${mapping.id}/`);
            } catch (error) {
              console.error(`Error deleting mapping ${mapping.id}:`, error);
              // Don't show error toast for delete failures, just log
            }
          }
        }
        
        // Add or update mappings for selected documents
        if (formData.selectedDocuments.length > 0) {
          for (const selectedDoc of formData.selectedDocuments) {
            const docId = selectedDoc.document_id;
            const existingMapping = existingMappings.find(m => {
              const mappingDocId = m.document?.id || m.document_id || m.document;
              return mappingDocId === docId;
            });
            
            if (existingMapping) {
              // Update existing mapping only if is_required changed
              if (existingMapping.is_required !== selectedDoc.is_required) {
                try {
                  await apiClient.patch(`/job-role-documents/${existingMapping.id}/`, {
                    is_required: selectedDoc.is_required !== false
                  });
                } catch (error) {
                  console.error(`Error updating mapping ${existingMapping.id}:`, error);
                  documentMappingErrors.push(`Failed to update document mapping: ${error.message || 'Unknown error'}`);
                }
              }
            } else {
              // Try to find if mapping already exists before creating
              // First check if mapping exists (might have been created elsewhere)
              try {
                const checkResponse = await apiClient.get(`/job-role-documents/?job_role_id=${roleId}`);
                const checkMappings = Array.isArray(checkResponse) ? checkResponse : (checkResponse.results || []);
                const foundMapping = checkMappings.find(m => {
                  const mappingDocId = m.document?.id || m.document_id || m.document;
                  return mappingDocId === docId;
                });
                
                if (foundMapping) {
                  // Mapping exists, update it using PATCH
                  await apiClient.patch(`/job-role-documents/${foundMapping.id}/`, {
                    is_required: selectedDoc.is_required !== false
                  });
                } else {
                  // Create new mapping using POST with correct field names
                  const payload = {
                    job_role_id: roleId,
                    document_id: docId,
                    is_required: selectedDoc.is_required !== false
                  };
                  await apiClient.post('/job-role-documents/', payload);
                }
              } catch (error) {
                console.error(`Error creating/updating mapping for document ${docId}:`, error);
                const errorMessage = error.data?.detail || error.data?.error || error.message || 'Unknown error';
                documentMappingErrors.push(`Failed to add document mapping: ${errorMessage}`);
              }
            }
          }
        }
      }
      
      // Refresh role documents
      if (roleId) {
        try {
          const response = await apiClient.get(API_ENDPOINTS.JOB_ROLES.DOCUMENTS(roleId));
          setRoleDocuments(prev => ({
            ...prev,
            [roleId]: response.mapped_documents || []
          }));
        } catch (error) {
          console.error('Error refreshing role documents:', error);
        }
      }
      
      handleCloseModal();
      
      // Show error toast only if document mappings failed
      // Note: Success toast is already shown by the mutation hook (useUpdateJobRole/useCreateJobRole)
      if (documentMappingErrors.length > 0) {
        // Show single error toast with summary
        showErrorToast(`Job role updated, but ${documentMappingErrors.length} document mapping(s) failed. Please check and try again.`);
      }
      // Don't show success toast here - the mutation hook already handles it
    } catch (error) {
      console.error('Error saving job role:', error);
      showErrorToast(error.message || 'Failed to save job role');
    }
  };

  const handleDelete = (id) => {
    setShowDeleteConfirm(id);
  };

  const handleConfirmDelete = () => {
    if (showDeleteConfirm) {
      deleteMutation.mutate(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getRoleDocuments = (roleId) => {
    return roleDocuments[roleId] || [];
  };

  if (isLoading) {
    return (
      <div className="admin-job-roles__loading">
        <div className="admin-job-roles__spinner"></div>
        <p>Loading job roles...</p>
      </div>
    );
  }

  return (
    <div className="admin-job-roles">
      <div className="admin-job-roles__header">
        <h1 className="admin-job-roles__title">Job Roles Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="admin-job-roles__add-btn"
        >
          + Add Job Role
        </button>
      </div>

      <div className="admin-job-roles__table-container">
        <table className="admin-job-roles__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="name" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Name
              </TableHeader>
              <TableHeader 
                sortKey="hourly_rate" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Hourly Rate
              </TableHeader>
              <TableHeader 
                sortKey="documents" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Required Documents
              </TableHeader>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedJobRoles && sortedJobRoles.length > 0 ? (
              sortedJobRoles.map((role) => {
                const docs = getRoleDocuments(role.id);
                return (
                  <tr key={role.id}>
                    <td>{role.job_role_name || role.name}</td>
                    <td>{formatCurrency(role.hourly_rate)}</td>
                    <td>
                      {loadingDocuments[role.id] ? (
                        <span>Loading...</span>
                      ) : docs.length > 0 ? (
                        <div className="admin-job-roles__documents">
                          {docs.map((mapping, idx) => (
                            <span 
                              key={idx} 
                              className={`admin-job-roles__doc-tag ${mapping.is_required ? 'admin-job-roles__doc-tag--required' : ''}`}
                            >
                              {mapping.document?.name || 'Unknown'}
                              {mapping.is_required && ' *'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        'None'
                      )}
                    </td>
                    <td>
                      <span className={`admin-job-roles__status admin-job-roles__status--${role.is_active ? 'active' : 'inactive'}`}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-job-roles__actions">
                        <button
                          onClick={() => handleOpenModal(role)}
                          className="admin-job-roles__btn admin-job-roles__btn--edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(role.id)}
                          className="admin-job-roles__btn admin-job-roles__btn--delete"
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="admin-job-roles__empty">No job roles found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="admin-job-roles__modal-overlay" onClick={handleCloseModal}>
          <div className="admin-job-roles__modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-job-roles__modal-header">
              <h2>{editingRole ? 'Edit Job Role' : 'Add Job Role'}</h2>
              <button onClick={handleCloseModal} className="admin-job-roles__modal-close">×</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-job-roles__form">
              {/* Basic Information Section */}
              <div className="admin-job-roles__form-section">
                <h3 className="admin-job-roles__form-section-title">Basic Information</h3>
                
                <div className="admin-job-roles__field">
                  <label>Job Role Key (unique identifier)</label>
                  <input
                    type="text"
                    value={formData.job_role_key}
                    onChange={(e) => setFormData({ ...formData, job_role_key: e.target.value })}
                    placeholder="e.g., dental_therapist"
                    required={!editingRole}
                    disabled={!!editingRole}
                    pattern="[a-z0-9_]+"
                    title="Lowercase letters, numbers, and underscores only"
                  />
                  <small>Lowercase letters, numbers, and underscores only. Auto-generated if left empty.</small>
                </div>
                
                <div className="admin-job-roles__field">
                  <label>Job Role Name</label>
                  <input
                    type="text"
                    value={formData.job_role_name}
                    onChange={(e) => setFormData({ ...formData, job_role_name: e.target.value })}
                    placeholder="e.g., Dental Therapist"
                    required
                  />
                </div>
                
                <div className="admin-job-roles__field">
                  <label>Hourly Rate (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                
                <div className="admin-job-roles__field">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Active Status
                  </label>
                  <small>Inactive roles will not appear in signup dropdowns</small>
                </div>
              </div>
              
              {/* Required Documents Section */}
              <div className="admin-job-roles__form-section">
                <h3 className="admin-job-roles__form-section-title">Required Documents</h3>
                <small style={{ marginTop: '-1rem', marginBottom: '0.5rem', display: 'block', color: 'var(--text-secondary)' }}>
                  Select documents required for this job role. Toggle "Required" to mark as mandatory.
                </small>
                
                <div className="admin-job-roles__documents-list">
                  {documentTypes && documentTypes.length > 0 ? (
                    documentTypes.map((doc) => {
                      const isSelected = formData.selectedDocuments.some(d => d.document_id === doc.id);
                      const selectedDoc = formData.selectedDocuments.find(d => d.document_id === doc.id);
                      
                      return (
                        <div key={doc.id} className="admin-job-roles__document-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleDocumentToggle(doc.id, true)}
                            />
                            <span>{doc.name}</span>
                          </label>
                          {isSelected && (
                            <label className="admin-job-roles__required-toggle">
                              <input
                                type="checkbox"
                                checked={selectedDoc?.is_required !== false}
                                onChange={() => handleDocumentRequiredToggle(doc.id)}
                                className="admin-job-roles__switch-input"
                              />
                              <span className="admin-job-roles__switch-slider"></span>
                              <span className="admin-job-roles__switch-label">Required</span>
                            </label>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="admin-job-roles__documents-empty">
                      <p>No document types available</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-job-roles__modal-actions">
                <button type="button" onClick={handleCloseModal} className="admin-job-roles__btn admin-job-roles__btn--cancel">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="admin-job-roles__btn admin-job-roles__btn--save"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <svg className="admin-job-roles__btn-spinner" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    editingRole ? 'Update Job Role' : 'Create Job Role'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        title="Delete Job Role"
        message="Are you sure you want to delete this job role? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(null)}
      />
    </div>
  );
};

export default AdminJobRoles;
